// File: src/pages/Dashboard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

/** ✅ Only TWO card types */
const CARD_TEMPLATES = {
  card1: { key: "card1", label: "Card 1", cols: 2 },
  card2: { key: "card2", label: "Card 2", cols: 4 },
};

/** ✅ Per-card-type max boxes */
const MAX_BOXES = {
  card1: 6,
  card2: 12,
};

/** ✅ FIXED sizes (no settings panel) */
const TEMPLATE_DESIGN = {
  card1: {
    cardW: 360,
    cardH: 260,
    cardPad: 7,
    boxW: 160,
    boxH: 50,
    boxGap: 8,
    boxRadius: 12,
  },
  card2: {
    cardW: 740,
    cardH: 260,
    cardPad: 7,
    boxW: 170,
    boxH: 50,
    boxGap: 8,
    boxRadius: 18,
  },
};

const DEFAULT_GLOBAL = { gridGap: 14, cardRadius: 16 };

const DEFAULT_CARDS = [
  { id: "c1", title: "Card 1", template: "card1", boxesCount: 0 },
  { id: "c2", title: "Card 2", template: "card2", boxesCount: 0 },
];

const LS_CARDS = "dash_cards_two_fixed_types_v1";
const LS_GLOBAL = "dash_global_two_fixed_types_v1";

const safeJsonParse = (v, fallback) => {
  try {
    const out = JSON.parse(v);
    return out ?? fallback;
  } catch {
    return fallback;
  }
};

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

const injectCssOnce = () => {
  if (document.getElementById("dash-template-fixed-css")) return;

  const s = document.createElement("style");
  s.id = "dash-template-fixed-css";
  s.textContent = `
    :root{
      --dash-bg:#f6f8ff;
      --dash-text:#0b1220;
      --dash-muted:#55627a;
    }

    html, body, #root{
      height:auto;
      margin:0;
      overflow:auto;
    }

    .dash-page{
      --parent-pl: 0px;
      --parent-pr: 0px;

      width: calc(100% + var(--parent-pl) + var(--parent-pr));
      margin-left: calc(-1 * var(--parent-pl));
      box-sizing: border-box;

      min-height: 100vh;
      color: var(--dash-text);

      background:
        radial-gradient(950px 520px at 10% 0%, rgba(99,102,241,.20), transparent 60%),
        radial-gradient(900px 520px at 92% 8%, rgba(236,72,153,.16), transparent 58%),
        radial-gradient(760px 520px at 40% 100%, rgba(34,197,94,.12), transparent 60%),
        linear-gradient(180deg, var(--dash-bg), #ffffff);
    }

    .dash-container{
      width:100%;
      min-height: 100vh;
      padding: 14px 14px 16px;
      box-sizing:border-box;
      display:flex;
      flex-direction:column;
      gap:12px;
    }

    .dash-toolbar{
      display:flex;
      align-items:flex-start;
      justify-content:space-between;
      gap:12px;
      padding:10px 12px;
      border-radius:14px;
      background: rgba(255,255,255,.65);
      border: 1px solid rgba(15,23,42,.08);
      backdrop-filter: blur(10px);
      box-shadow: 0 10px 24px rgba(2,6,23,.06);
    }

    .dash-toolbar .left{
      display:flex;
      align-items:center;
      gap:10px;
      flex-wrap:wrap;
    }

    .dash-label{ font-weight:950; color:var(--dash-text); white-space:nowrap; }

    .dash-input{
      height:34px;
      border-radius:10px;
      padding:0 10px;
      border: 1px solid rgba(15,23,42,.12);
      background: rgba(255,255,255,.92);
      color: var(--dash-text);
      outline:none;
      min-width: 220px;
    }

    .dash-btn{
      height:34px;
      border-radius:10px;
      padding:0 12px;
      border: 1px solid rgba(15,23,42,.10);
      background: rgba(255,255,255,.92);
      font-weight:950;
      cursor:pointer;
      white-space:nowrap;
      user-select:none;
    }
    .dash-btn:hover{
      transform: translateY(-1px);
      box-shadow: 0 10px 20px rgba(2,6,23,.08);
    }
    .dash-btn:disabled{
      opacity:.55;
      cursor:not-allowed;
      transform:none;
      box-shadow:none;
    }

    .dash-count{
      font-weight:950;
      color:var(--dash-muted);
      white-space:nowrap;
      padding-top:4px;
    }

    .addgrp{
      display:flex;
      align-items:center;
      gap:6px;
      padding: 4px;
      border-radius: 12px;
      background: rgba(255,255,255,.50);
      border: 1px solid rgba(15,23,42,.08);
    }

    .iconbtn{
      width:34px;
      height:34px;
      border-radius:10px;
      border: 1px solid rgba(15,23,42,.10);
      background: rgba(255,255,255,.92);
      cursor:pointer;
      font-weight: 950;
      display:flex;
      align-items:center;
      justify-content:center;
      user-select:none;
    }
    .iconbtn:hover{
      transform: translateY(-1px);
      box-shadow: 0 10px 20px rgba(2,6,23,.08);
    }
    .iconbtn:disabled{
      opacity:.55;
      cursor:not-allowed;
      transform:none;
      box-shadow:none;
    }
    .iconbtn.danger{ color:#991b1b; }

    .ctl{
      display:flex;
      align-items:center;
      gap:8px;
      padding: 6px 8px;
      border-radius: 12px;
      background: rgba(255,255,255,.70);
      border: 1px solid rgba(15,23,42,.08);
    }
    .ctl .k{
      font-size:12px;
      font-weight:950;
      color:var(--dash-muted);
      white-space:nowrap;
    }
    .ctl input[type="range"]{ width: 120px; }
    .ctl input[type="number"]{
      width: 70px;
      height: 30px;
      border-radius: 10px;
      padding: 0 8px;
      border: 1px solid rgba(15,23,42,.12);
      background: rgba(255,255,255,.92);
      outline:none;
      font-weight: 900;
      color: var(--dash-text);
    }

    .dash-grid{
      display:flex;
      flex-wrap:wrap;
      gap: var(--grid-gap, 14px);
      align-items: stretch;
      align-content:flex-start;
    }

    .dash-card{
      flex: 0 0 min(var(--card-w, 360px), 100%);
      min-height: var(--card-h, 240px);
      border-radius: var(--card-radius, 16px);
      padding: var(--card-pad, 12px);
      background: rgba(255,255,255,.74);
      border: 1px solid rgba(15,23,42,.08);
      box-shadow: 0 12px 30px rgba(2,6,23,.06);
      overflow:hidden;
      position:relative;
      box-sizing:border-box;

      display:flex;
      flex-direction:column;
    }

    .dash-card:before{
      content:"";
      position:absolute;
      inset:-40px -40px auto auto;
      width:120px;
      height:120px;
      border-radius:999px;
      background: radial-gradient(circle, rgba(99,102,241,.18), transparent 60%);
      pointer-events:none;
    }

    .dash-title{
      margin:0;
      font-weight:950;
      font-size:13px;
      letter-spacing:.2px;
      color: var(--dash-text);
      text-align:center;
      position:relative;
      z-index:1;
    }

    .dash-title-edit{
      width:100%;
      height:34px;
      margin-top:10px;
      border-radius:10px;
      padding:0 10px;
      border: 1px solid rgba(15,23,42,.12);
      background: rgba(255,255,255,.95);
      outline:none;
      font-weight: 900;
      color: var(--dash-text);
      box-sizing:border-box;
      position:relative;
      z-index:1;
    }

    .card-addmore{
      margin-top: 10px;
      display:flex;
      justify-content:center;
      gap:10px;
      position:relative;
      z-index:1;
    }
    .card-addmore button{
      height: 30px;
      border-radius: 10px;
      padding: 0 10px;
      border: 1px solid rgba(15,23,42,.10);
      background: rgba(255,255,255,.92);
      font-weight: 950;
      cursor: pointer;
      user-select:none;
    }
    .card-addmore button.rm{
      color:#991b1b;
      border-color: rgba(153,27,27,.22);
    }
    .card-addmore button:disabled{
      opacity:.55;
      cursor:not-allowed;
    }

    .mini-grid{
      margin-top:10px;
      display:grid;
      gap: var(--box-gap, 10px);
      justify-content:center;
      position:relative;
      z-index:1;
    }

    .mini-cell{
      width: var(--box-w-eff, 90px);
      height: var(--box-h, 34px);
      border-radius: var(--box-radius, 4px);
      border: 1px solid rgba(15,23,42,.50);
      background: rgba(255,255,255,.92);
      box-sizing:border-box;
    }
    .mini-cell.filled{
      background: rgba(99,102,241,.12);
      border-color: rgba(99,102,241,.40);
    }
    .mini-cell.add{
      border-style: dashed;
      cursor:pointer;
      display:flex;
      align-items:center;
      justify-content:center;
      font-weight: 950;
      color: rgba(15,23,42,.85);
      user-select:none;
    }
    .mini-cell.add.disabled{
      opacity:.55;
      cursor:not-allowed;
    }

    /* ✅ icons at bottom */
    .card-actions{
      margin-top: auto;
      padding-top: 10px;
      display:flex;
      justify-content:center;
      gap:8px;
      position:relative;
      z-index:2;
    }

    /* Keep hidden as you requested earlier */
    .dash-sub{ display:none !important; }
    .mini-footer{ display:none !important; }
  `;
  document.head.appendChild(s);
};

function getMaxBoxesForTemplate(templateKey) {
  return MAX_BOXES[templateKey] ?? 6;
}

function normalizeCards(arr) {
  const list = Array.isArray(arr) ? arr : DEFAULT_CARDS;
  return list.map((c, idx) => {
    const template = CARD_TEMPLATES[c.template] ? c.template : "card1";
    const max = getMaxBoxesForTemplate(template);

    const boxesCount =
      typeof c.boxesCount === "number"
        ? c.boxesCount
        : Array.isArray(c.boxes)
        ? c.boxes.length
        : 0;

    return {
      id: c.id || `c_${Date.now()}_${idx}`,
      title: String(c.title || CARD_TEMPLATES[template].label),
      template,
      boxesCount: clamp(Math.max(0, boxesCount), 0, max),
    };
  });
}

function useResizeWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setW(el.getBoundingClientRect().width || 0);
    update();

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => update());
      ro.observe(el);
    } else {
      window.addEventListener("resize", update);
    }

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", update);
    };
  }, []);

  return [ref, w];
}

function CardBox({
  card,
  global,
  onRename,
  onRemove,
  onAddBox,
  onRemoveBox,
  editingId,
  setEditingId,
}) {
  const tpl = CARD_TEMPLATES[card.template] || CARD_TEMPLATES.card1;
  const cols = Math.max(1, tpl.cols);

  const design = TEMPLATE_DESIGN[card.template] || TEMPLATE_DESIGN.card1;

  const [wrapRef, wrapW] = useResizeWidth();

  const maxBoxes = getMaxBoxesForTemplate(card.template);
  const filledCount = clamp(Math.max(0, card.boxesCount), 0, maxBoxes);
  const canAdd = filledCount < maxBoxes;

  // Fit boxes to available width
  const contentW = Math.max(0, wrapW - design.cardPad * 2);
  const maxFit = (contentW - (cols - 1) * design.boxGap) / cols;
  const boxWEff = clamp(Math.min(design.boxW, maxFit || design.boxW), 10, 9999);

  // Grid cells: filled + optional add + empties to fill last row
  const addCells = canAdd ? 1 : 0;
  const totalWithAdd = Math.max(1, filledCount + addCells);
  const rows = Math.ceil(totalWithAdd / cols);
  const totalCells = rows * cols;
  const addIndex = filledCount;

  const cells = Array.from({ length: totalCells }).map((_, i) => {
    if (i < filledCount) return { kind: "filled", key: `f_${i}` };
    if (canAdd && i === addIndex) return { kind: "add", key: `a_${i}` };
    return { kind: "empty", key: `e_${i}` };
  });

  const cardStyle = {
    "--card-w": `${design.cardW}px`,
    "--card-h": `${design.cardH}px`,
    "--card-pad": `${design.cardPad}px`,
    "--card-radius": `${global.cardRadius}px`,
  };

  const miniGridStyle = {
    "--box-w-eff": `${boxWEff}px`,
    "--box-h": `${design.boxH}px`,
    "--box-gap": `${design.boxGap}px`,
    "--box-radius": `${design.boxRadius}px`,
    gridTemplateColumns: `repeat(${cols}, ${boxWEff}px)`,
  };

  return (
    <div className="dash-card" ref={wrapRef} style={cardStyle}>
      <p className="dash-title">{card.title}</p>

      {editingId === card.id && (
        <input
          className="dash-title-edit"
          autoFocus
          value={card.title}
          onChange={(e) => onRename(card.id, e.target.value)}
          onBlur={() => setEditingId(null)}
          onKeyDown={(e) => e.key === "Enter" && setEditingId(null)}
        />
      )}

      <div className="mini-grid" style={miniGridStyle}>
        {cells.map((cell) => {
          if (cell.kind === "filled")
            return <div key={cell.key} className="mini-cell filled" />;
          if (cell.kind === "add")
            return (
              <div
                key={cell.key}
                className={`mini-cell add ${!canAdd ? "disabled" : ""}`}
                onClick={() => canAdd && onAddBox(card.id)}
                title={canAdd ? "Add box" : `Max ${maxBoxes} boxes`}
              >
                +
              </div>
            );
          return <div key={cell.key} className="mini-cell" />;
        })}
      </div>

      <div className="card-actions">
        <button
          className="iconbtn"
          title="Add box"
          onClick={() => onAddBox(card.id)}
          disabled={!canAdd}
        >
          ＋
        </button>
        <button
          className="iconbtn danger"
          title="Remove box"
          onClick={() => onRemoveBox(card.id)}
          disabled={filledCount === 0}
        >
          −
        </button>
        <button
          className="iconbtn"
          title="Rename"
          onClick={() => setEditingId((p) => (p === card.id ? null : card.id))}
        >
          ✎
        </button>
        <button
          className="iconbtn"
          title="Remove Card"
          onClick={() => onRemove(card.id)}
        >
          ×
        </button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const pageRef = useRef(null);

  const [cards, setCards] = useState(() => {
    const saved = localStorage.getItem(LS_CARDS);
    const parsed = saved ? safeJsonParse(saved, DEFAULT_CARDS) : DEFAULT_CARDS;
    return normalizeCards(parsed);
  });

  const [global, setGlobal] = useState(() => {
    const saved = localStorage.getItem(LS_GLOBAL);
    const parsed = saved
      ? safeJsonParse(saved, DEFAULT_GLOBAL)
      : DEFAULT_GLOBAL;
    return {
      gridGap: clamp(Number(parsed.gridGap ?? DEFAULT_GLOBAL.gridGap), 6, 40),
      cardRadius: clamp(
        Number(parsed.cardRadius ?? DEFAULT_GLOBAL.cardRadius),
        8,
        28
      ),
    };
  });

  const [newTitle, setNewTitle] = useState("");
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    injectCssOnce();

    // ✅ parent padding fix (left/right alignment)
    const applyParentPaddingFix = () => {
      const el = pageRef.current;
      const parent = el?.parentElement;
      if (!el || !parent) return;

      const cs = window.getComputedStyle(parent);
      const pl = parseFloat(cs.paddingLeft || "0") || 0;
      const pr = parseFloat(cs.paddingRight || "0") || 0;

      el.style.setProperty("--parent-pl", `${pl}px`);
      el.style.setProperty("--parent-pr", `${pr}px`);
    };

    const raf = requestAnimationFrame(applyParentPaddingFix);
    window.addEventListener("resize", applyParentPaddingFix);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", applyParentPaddingFix);
    };
  }, []);

  useEffect(
    () => localStorage.setItem(LS_CARDS, JSON.stringify(cards)),
    [cards]
  );
  useEffect(
    () => localStorage.setItem(LS_GLOBAL, JSON.stringify(global)),
    [global]
  );

  const rootVars = useMemo(
    () => ({
      "--grid-gap": `${global.gridGap}px`,
      "--card-radius": `${global.cardRadius}px`,
    }),
    [global.gridGap, global.cardRadius]
  );

  const addCard = (templateKey) => {
    const tpl = CARD_TEMPLATES[templateKey] ? templateKey : "card1";
    const t = newTitle.trim();
    const title = t || CARD_TEMPLATES[tpl].label;

    setCards((prev) => [
      ...prev,
      { id: `c_${Date.now()}`, title, template: tpl, boxesCount: 0 },
    ]);
    setNewTitle("");
  };

  const removeCard = (id) => {
    setCards((prev) => prev.filter((c) => c.id !== id));
    setEditingId((p) => (p === id ? null : p));
  };

  const renameCard = (id, title) =>
    setCards((prev) => prev.map((c) => (c.id === id ? { ...c, title } : c)));

  const addBoxInCard = (cardId) => {
    setCards((prev) =>
      prev.map((c) => {
        if (c.id !== cardId) return c;
        const max = getMaxBoxesForTemplate(c.template);
        const cur = clamp(c.boxesCount || 0, 0, max);
        if (cur >= max) return c;
        return { ...c, boxesCount: cur + 1 };
      })
    );
  };

  const removeBoxInCard = (cardId) => {
    setCards((prev) =>
      prev.map((c) => {
        if (c.id !== cardId) return c;
        const max = getMaxBoxesForTemplate(c.template);
        const cur = clamp(c.boxesCount || 0, 0, max);
        return { ...c, boxesCount: Math.max(0, cur - 1) };
      })
    );
  };

  return (
    <div ref={pageRef} className="dash-page">
      <div className="dash-container" style={rootVars}>
        <div className="dash-toolbar">
          <div style={{ width: "100%" }}>
            <div className="left">
              <div className="dash-label">Cards</div>

              <input
                className="dash-input"
                value={newTitle}
                placeholder="Optional card title..."
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCard("card1")}
              />

              <div className="addgrp">
                <button className="dash-btn" onClick={() => addCard("card1")}>
                  Add Card 1
                </button>
              </div>

              <div className="addgrp">
                <button className="dash-btn" onClick={() => addCard("card2")}>
                  Add Card 2
                </button>
              </div>

              <div className="ctl" title="Gap between cards">
                <span className="k">Grid Gap</span>
                <input
                  type="range"
                  min={6}
                  max={40}
                  value={global.gridGap}
                  onChange={(e) =>
                    setGlobal((p) => ({
                      ...p,
                      gridGap: clamp(Number(e.target.value), 6, 40),
                    }))
                  }
                />
                <input
                  type="number"
                  value={global.gridGap}
                  onChange={(e) =>
                    setGlobal((p) => ({
                      ...p,
                      gridGap: clamp(Number(e.target.value || 0), 6, 40),
                    }))
                  }
                />
              </div>

              <div className="ctl" title="Card radius">
                <span className="k">Radius</span>
                <input
                  type="range"
                  min={8}
                  max={28}
                  value={global.cardRadius}
                  onChange={(e) =>
                    setGlobal((p) => ({
                      ...p,
                      cardRadius: clamp(Number(e.target.value), 8, 28),
                    }))
                  }
                />
                <input
                  type="number"
                  value={global.cardRadius}
                  onChange={(e) =>
                    setGlobal((p) => ({
                      ...p,
                      cardRadius: clamp(Number(e.target.value || 0), 8, 28),
                    }))
                  }
                />
              </div>
            </div>
          </div>

          <div className="dash-count">Total: {cards.length}</div>
        </div>

        <div className="dash-grid">
          {cards.map((c) => (
            <CardBox
              key={c.id}
              card={c}
              global={global}
              onRename={renameCard}
              onRemove={removeCard}
              onAddBox={addBoxInCard}
              onRemoveBox={removeBoxInCard}
              editingId={editingId}
              setEditingId={setEditingId}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
